import React, { useEffect, useState } from 'react';
import { Card, Col, Dropdown, Row, Spinner, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { jwtDecode } from 'jwt-decode';
import { useLocation, useNavigate } from 'react-router-dom';
import Flex from '../../../common/Flex';
import CardDropdown from '../../../common/CardDropdown';
import { useAppContext } from '../../../../providers/AppProvider';
import { itemMatchRosterCounts, listMatches } from '../../../../lib/api/match';
import PlayerRosterModal from './PlayerRosterModal';
import StaffRosterModal from './StaffRosterModal';
import LicenseOverviewModal from './LicenseOverviewModal';
import MatchScoreboard from '../MatchScoreboard';
import paths from '../../../../routes/paths';

const formatMatchTime = value => (value ? String(value).slice(0, 5) : '');

const hasReport = item =>
  item.match_reports_id !== null && item.match_reports_id !== undefined && item.match_reports_id !== '';

export const ActiveMatches = () => {
  const { userApp } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRosterAction, setSelectedRosterAction] = useState(null);
  const [showPlayerRosterModal, setShowPlayerRosterModal] = useState(false);
  const [showStaffRosterModal, setShowStaffRosterModal] = useState(false);
  const [showLicenseOverviewModal, setShowLicenseOverviewModal] = useState(false);
  const [selectedMatchForLicenses, setSelectedMatchForLicenses] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;

    const scrollKey = `active-matches-scroll:${location.key}`;
    const savedPosition = sessionStorage.getItem(scrollKey);
    if (savedPosition === null) return;

    const frame = requestAnimationFrame(() => {
      window.scrollTo({ top: Number(savedPosition), behavior: 'instant' });
      sessionStorage.removeItem(scrollKey);
    });

    return () => cancelAnimationFrame(frame);
  }, [loading, location.key]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await listMatches({
        page: 1,
        limit: 1000,
        is_active: 1,
        scope: 'assigned'
      });

      setItems(response?.items || []);
    } catch (error) {
      console.error('Greska prilikom ucitavanja aktivnih utakmica:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const roleTypeId = userApp?.role_type_id;
  const token = localStorage.getItem('token');
  const decodedToken = token ? jwtDecode(token) : null;
  const operatorEntitiesId = Number(decodedToken?.operator_entities_id);
  const tokenEntitiesId = Number(decodedToken?.entities_id);
  const canViewLicenses = ['admin', 'supervisor', 'judge'].includes(roleTypeId);
  const canManageReports = item =>
    ['admin', 'supervisor'].includes(roleTypeId) ||
    (roleTypeId === 'judge' &&
      (item?.supervisor_entities_id === null ||
        item?.supervisor_entities_id === undefined));
  const canSeeActionColumn = ['admin', 'clubs', 'staff', 'supervisor', 'judge'].includes(roleTypeId);

  const getClubLabel = (item, clubKey) =>
    item[clubKey] || item[clubKey.replace('_short_', '_')];

  const handleOpenRosterModal = action => {
    setSelectedRosterAction(action);

    if (action.role === 'player') {
      setShowPlayerRosterModal(true);
      return;
    }

    setShowStaffRosterModal(true);
  };

  const closePlayerRosterModal = () => {
    setShowPlayerRosterModal(false);
    setSelectedRosterAction(null);
  };

  const closeStaffRosterModal = () => {
    setShowStaffRosterModal(false);
    setSelectedRosterAction(null);
  };

  const handleOpenLicenseOverviewModal = item => {
    setSelectedMatchForLicenses(item);
    setShowLicenseOverviewModal(true);
  };

  const closeLicenseOverviewModal = () => {
    setShowLicenseOverviewModal(false);
    setSelectedMatchForLicenses(null);
  };

  const handleOpenMatchReport = item => {
    sessionStorage.setItem(`active-matches-scroll:${location.key}`, String(window.scrollY));
    navigate(paths.matchReport(item.matches_guid), {
      state: {
        from: `${location.pathname}${location.search || ''}`,
        matchItem: item
      }
    });
  };

  const refreshRosterCounts = async matchesGuid => {
    if (!matchesGuid) {
      return;
    }

    try {
      const response = await itemMatchRosterCounts(matchesGuid);
      const countsItem = Array.isArray(response) ? response[0] : response;

      if (!countsItem) {
        return;
      }

      setItems(prev =>
        prev.map(item =>
          item.matches_guid === matchesGuid
            ? {
                ...item,
                home_players: countsItem.home_players,
                away_players: countsItem.away_players,
                home_staff: countsItem.home_staff,
                away_staff: countsItem.away_staff
              }
            : item
        )
      );
    } catch (error) {
      console.error('Greska prilikom osvjezavanja roster brojaca:', error);
    }
  };

  const getRosterActions = item => {
    const club1Player = {
      key: 'club1-players',
      label: `Unos igraca - ${getClubLabel(item, 'club_1_short_name')}`,
      role: 'player',
      clubGuid: item.club_1_guid,
      clubImagePath: item.club_1_image_path,
      clubEntitiesId: item.club_entities_id_1,
      teamSide: 'home',
      matchId: item.match_id,
      competitionsGuid: item.competitions_guid,
      matchesGuid: item.matches_guid
    };

    const club1Staff = {
      key: 'club1-staff',
      label: `Unos trenera - ${getClubLabel(item, 'club_1_short_name')}`,
      role: 'staff',
      clubGuid: item.club_1_guid,
      clubImagePath: item.club_1_image_path,
      clubEntitiesId: item.club_entities_id_1,
      teamSide: 'home',
      matchId: item.match_id,
      competitionsGuid: item.competitions_guid,
      matchesGuid: item.matches_guid
    };

    const club2Player = {
      key: 'club2-players',
      label: `Unos igraca - ${getClubLabel(item, 'club_2_short_name')}`,
      role: 'player',
      clubGuid: item.club_2_guid,
      clubImagePath: item.club_2_image_path,
      clubEntitiesId: item.club_entities_id_2,
      teamSide: 'away',
      matchId: item.match_id,
      competitionsGuid: item.competitions_guid,
      matchesGuid: item.matches_guid
    };

    const club2Staff = {
      key: 'club2-staff',
      label: `Unos trenera - ${getClubLabel(item, 'club_2_short_name')}`,
      role: 'staff',
      clubGuid: item.club_2_guid,
      clubImagePath: item.club_2_image_path,
      clubEntitiesId: item.club_entities_id_2,
      teamSide: 'away',
      matchId: item.match_id,
      competitionsGuid: item.competitions_guid,
      matchesGuid: item.matches_guid
    };

    if (roleTypeId === 'admin') {
      return [
        club1Player,
        club1Staff,
        { key: 'divider-club1', divider: true },
        club2Player,
        club2Staff
      ];
    }

    if (
      roleTypeId === 'staff' &&
      Number(item.staff_entities_id) === operatorEntitiesId
    ) {
      return [
        club1Player,
        club1Staff,
        { key: 'divider-club1', divider: true },
        club2Player,
        club2Staff
      ];
    }

    if (roleTypeId === 'clubs') {
      if (Number(item.club_entities_id_1) === tokenEntitiesId) {
        return [club1Player, club1Staff];
      }

      if (Number(item.club_entities_id_2) === tokenEntitiesId) {
        return [club2Player, club2Staff];
      }
    }

    return [];
  };

  const renderActions = item => {
    const rosterActions = getRosterActions(item);
    const canManageItemReport = canManageReports(item);
    const hasActions =
      rosterActions.length > 0 || canViewLicenses || canManageItemReport;

    if (!hasActions) {
      return null;
    }

    return (
      <CardDropdown>
        <div className="py-2">
          {rosterActions.map(action =>
            action.divider ? (
              <Dropdown.Divider key={action.key} />
            ) : (
              <Dropdown.Item
                key={action.key}
                onClick={() => handleOpenRosterModal(action)}
              >
                {action.label}
              </Dropdown.Item>
            )
          )}
          {rosterActions.length > 0 && canViewLicenses && <Dropdown.Divider />}
          {canViewLicenses && (
            <Dropdown.Item onClick={() => handleOpenLicenseOverviewModal(item)}>
              Pregled licenci
            </Dropdown.Item>
          )}
          {canManageItemReport && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => handleOpenMatchReport(item)}>
                Unos Izvještaja
              </Dropdown.Item>
            </>
          )}
        </div>
      </CardDropdown>
    );
  };

  return (
    <Row className="g-3">
      <Col xs={12}>
        <Card className={classNames('h-100')}>
          <Card.Header>
            <Row className={classNames('g-2')}>
              <Col>
                <Flex>
                  <h5 className="mb-0 hover-actions-trigger text-truncate text-nowrap">
                    Aktivne utakmice
                  </h5>
                </Flex>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="bg-body-tertiary">
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <Row className="mb-3">
                <div className="d-none d-xl-block">
                  <Table responsive size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Takmičenja</th>
                        <th className="text-center">Datum</th>
                        <th>Parovi / Rezultat</th>
                        <th>Igrači</th>
                        <th>Klupa</th>
                        <th className="text-center">Aktivan</th>
                        <th className="text-center" title="Izvještaj" />
                        {canSeeActionColumn && (
                          <th className="text-end">Akcija</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="fs-10">
                      {items.map(item => {
                        const hasActions =
                          getRosterActions(item).length > 0 ||
                          canViewLicenses ||
                          canManageReports(item);

                        return (
                          <tr key={item.matches_guid || item.match_id}>
                            <td>
                              <div className="ms-2">{item.match_id}</div>
                            </td>
                            <td>
                              <div className="ms-2">
                                <div>{item.competitions_name}</div>
                                <div className="text-body-tertiary">
                                  {item.round_type_name}
                                </div>
                              </div>
                            </td>
                            <td className="text-center align-middle text-nowrap">
                              <div>
                                <div>
                                  {item.date
                                    ? format(parseISO(item.date), 'dd. MMM yy.')
                                    : ''}
                                </div>
                                <div className="text-body-tertiary">
                                  {formatMatchTime(item.match_time)}
                                </div>
                              </div>
                            </td>
                            <td>
                              <MatchScoreboard item={item} />
                            </td>
                            <td>
                              <div
                                className="ms-2"
                                style={{ paddingTop: '5px', paddingBottom: '5px' }}
                              >
                                <div>
                                  {Number(item.home_players) === 0
                                    ? '-'
                                    : item.home_players || ''}
                                </div>
                                <div className="text-body-tertiary mt-2">
                                  {Number(item.away_players) === 0
                                    ? '-'
                                    : item.away_players || ''}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div
                                className="ms-2"
                                style={{ paddingTop: '5px', paddingBottom: '5px' }}
                              >
                                <div>
                                  {Number(item.home_staff) === 0
                                    ? '-'
                                    : item.home_staff || ''}
                                </div>
                                <div className="text-body-tertiary mt-2">
                                  {Number(item.away_staff) === 0
                                    ? '-'
                                    : item.away_staff || ''}
                                </div>
                              </div>
                            </td>
                            <td className="text-center align-middle">
                              {Number(item.is_active) === 1 ? (
                                <FontAwesomeIcon icon="eye" />
                              ) : null}
                            </td>
                            <td className="text-center align-middle">
                              {hasReport(item) && (
                                <FontAwesomeIcon icon="check-circle" className="text-success fs-8" title="Izvještaj postoji" />
                              )}
                            </td>
                            {hasActions && (
                              <td className="text-end">{renderActions(item)}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-xl-none">
                  <Row className="g-3">
                    {items.map(item => (
                      <Col xs={12} key={item.matches_guid || item.match_id}>
                        <div
                          className="border rounded-4 bg-white h-100 p-3 shadow-sm"
                          style={{ borderColor: '#dfe3ee' }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="fw-semibold text-dark fs-9">
                                {item.competitions_name || '-'}
                              </div>
                            </div>
                            {renderActions(item) && (
                              <div className="flex-shrink-0">{renderActions(item)}</div>
                            )}
                          </div>

                          <div className="d-flex flex-wrap gap-2 mt-2">
                            <span className="badge rounded-pill badge-subtle-primary">
                              #{item.match_id || '-'}
                            </span>
                            <span className="badge rounded-pill badge-subtle-success">
                              {item.round_type_name || '-'}
                            </span>
                            {Number(item.is_active) === 1 && (
                              <span className="badge rounded-pill badge-subtle-info">
                                Aktivan
                              </span>
                            )}
                            {hasReport(item) && (
                              <span className="badge rounded-pill badge-subtle-success">
                                <FontAwesomeIcon icon="check-circle" className="me-1" />
                                Izvještaj
                              </span>
                            )}
                          </div>

                          <div className="text-600 fs-10 mt-2">
                            {item.date ? format(parseISO(item.date), 'dd. MMM yy.') : '-'}
                            {item.match_time ? ` u ${formatMatchTime(item.match_time)}` : ''}
                          </div>

                          <div className="mt-2 fs-10">
                            <MatchScoreboard item={item} />
                          </div>

                          <div className="d-flex flex-wrap gap-3 mt-3 text-600 fs-10">
                            <span>
                              Igrači: {Number(item.home_players) === 0 ? '-' : item.home_players || '-'} / {Number(item.away_players) === 0 ? '-' : item.away_players || '-'}
                            </span>
                            <span>
                              Klupa: {Number(item.home_staff) === 0 ? '-' : item.home_staff || '-'} / {Number(item.away_staff) === 0 ? '-' : item.away_staff || '-'}
                            </span>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>

      <PlayerRosterModal
        show={showPlayerRosterModal}
        action={selectedRosterAction}
        onClose={closePlayerRosterModal}
        onSaved={refreshRosterCounts}
      />

      <StaffRosterModal
        show={showStaffRosterModal}
        action={selectedRosterAction}
        onClose={closeStaffRosterModal}
        onSaved={refreshRosterCounts}
      />

      <LicenseOverviewModal
        show={showLicenseOverviewModal}
        matchItem={selectedMatchForLicenses}
        onClose={closeLicenseOverviewModal}
      />
    </Row>
  );
};

export default ActiveMatches;
